import { getNotificationsBaseUrl, log, debug, sessionId } from './common'
import type { NotificationJson } from '../gen/creativefabrica/notifications/v1/types_pb'
import {
  setNotificationsStoreState,
  routeNotificationToStore,
  lastEvent,
  hasSubscriptionsForToken,
  notifyStreamConnected,
} from './notifications'
import { getBrowserLocation } from '@repo/env-graphql-url'
import { rpcUrlForLocation } from '@repo/env-graphql-url/rpc-connect-url'
import {
  getToken,
  getCurrentToken,
  invalidateToken,
  usesCookieAuth,
} from '@repo/rpc-codegen/fetch-rpc'

/**
 * The stream lives under the service name so the site-scoped services hostnames route it like
 * any RPC and the cookie can authenticate it at the gateway. The service also keeps the legacy
 * `/notifications` path; the client no longer uses it.
 */
export const STREAM_PATH = '/creativefabrica.notifications.v1.NotificationsService/notifications'

/** `undefined` identifies the cookie-authenticated stream; a string is the JWT of a token stream. */
export type StreamKey = string | undefined

const COOKIE_STREAM_KEY = 'cookie'

export function getNotificationStreamUrl(): string {
  return rpcUrlForLocation(`${getNotificationsBaseUrl()}${STREAM_PATH}`, getBrowserLocation())
}

export function streamUsesCookieAuth(): boolean {
  return usesCookieAuth(getNotificationStreamUrl())
}

export function getNotificationSSEUrl(sessionId: string, token?: string): string {
  const url = new URL(getNotificationStreamUrl())
  url.searchParams.set('sessionId', sessionId)
  if (token) {
    url.searchParams.set('token', token)
  }
  return url.href
}

interface EventSourceEntry {
  eventSource: EventSource
  state: 'connecting' | 'connected' | 'disconnected'
  reconnectAttempts: number
  lastError: Event | null
  connectionStartTime: number | null
  healthCheckInterval: NodeJS.Timeout | null
  stateCheckInterval: NodeJS.Timeout | null
  /** Identifies the stream in the maps and in every event entry; never the credential. */
  token: StreamKey
  /** The JWT the stream was last opened with; refreshed on reconnect. Absent on the cookie stream. */
  credential?: string
}

const eventSourceMap = new Map<string, EventSourceEntry>()
function getEventSourceMapKey(token?: StreamKey): string {
  return `${sessionId}-${token ?? COOKIE_STREAM_KEY}`
}
function getEventSourceEntry(token?: StreamKey): EventSourceEntry | undefined {
  return eventSourceMap.get(getEventSourceMapKey(token))
}

let reconnectTimeout: NodeJS.Timeout | null = null
let reconnectAttempts = 0

let healthCheckInterval: NodeJS.Timeout | null = null
let stateCheckInterval: NodeJS.Timeout | null = null

function patchEventSourceEntry(eventSourceEntry: Partial<EventSourceEntry> & { token: StreamKey }) {
  const current = getEventSourceEntry(eventSourceEntry.token)
  if (!current) {
    return null
  }
  const newEntry = {
    ...current,
    ...eventSourceEntry,
  }
  eventSourceMap.set(getEventSourceMapKey(eventSourceEntry.token), newEntry)
}

export async function connect(providedToken?: StreamKey, credential?: string) {
  const eventSourceEntry = getEventSourceEntry(providedToken)
  if (eventSourceEntry?.state === 'connecting' || eventSourceEntry?.state === 'connected') {
    return
  }

  try {
    const cookieAuth = streamUsesCookieAuth()
    // The key identifies the stream in the maps and is what callers hand around; the credential
    // is what the URL carries. They are the same JWT on a first open and differ after a reconnect
    // refresh. A caller without a token on a token host gets the `undefined` key and a fetched
    // credential, so its bookkeeping never depends on which JWT was fetched. The cookie stream
    // has neither.
    const token: StreamKey = cookieAuth ? undefined : providedToken
    let urlToken: string | undefined = cookieAuth ? undefined : (credential ?? providedToken)
    if (!cookieAuth && !urlToken) {
      await getToken()
      urlToken = getCurrentToken() || ''
    }
    // The first open announces nothing: the initial acknowledgement does. Every later stream
    // reaches here because `onerror` closes the EventSource, cancelling its native retry.
    const isReconnect = Boolean(getEventSourceEntry(token))
    const url = getNotificationSSEUrl(sessionId, urlToken)
    const newEventSource = new EventSource(url)
    const newEventSourceEntry: EventSourceEntry = {
      eventSource: newEventSource,
      state: 'connecting',
      reconnectAttempts: 0,
      lastError: null,
      connectionStartTime: Date.now(),
      healthCheckInterval: null,
      stateCheckInterval: null,
      token,
      credential: urlToken,
    }
    eventSourceMap.set(getEventSourceMapKey(token), newEventSourceEntry)

    newEventSource.onopen = () => {
      debug && log('SSE connection established successfully')
      newEventSourceEntry.state = 'connected'
      newEventSourceEntry.connectionStartTime = Date.now()
      newEventSourceEntry.reconnectAttempts = 0
      patchEventSourceEntry(newEventSourceEntry)
      clearReconnectTimeout()
      updateStoreState()
      if (isReconnect) {
        notifyStreamConnected(token)
      }
    }

    newEventSource.onerror = event => {
      newEventSourceEntry.state = 'disconnected'
      newEventSourceEntry.lastError = event
      patchEventSourceEntry(newEventSourceEntry)
      updateStoreState()

      if (newEventSource) {
        newEventSource.close()
      }

      if (
        event.type === 'error' &&
        newEventSource.readyState === EventSource.CLOSED &&
        !hasSubscriptionsForToken(token)
      ) {
        return
      }
      attemptReconnect(token)
    }

    newEventSource.addEventListener('notification', event => {
      try {
        const data = JSON.parse(event.data) as NotificationJson
        debug && log('Received notification:', data)
        updateStoreState()
        routeNotificationToStore(data)
        // oxlint-disable-next-line unicorn/catch-error-name
      } catch (err) {
        debug && log('Failed to parse notification', err)
      }
    })

    startConnectionHealthCheck(token)
    // oxlint-disable-next-line unicorn/catch-error-name
  } catch (err) {
    debug && log('Failed to create EventSource:', err)
    const eventSourceEntry = getEventSourceEntry(providedToken)
    if (eventSourceEntry?.eventSource) {
      eventSourceEntry.state = 'disconnected'
      eventSourceEntry.lastError = err as Event
      patchEventSourceEntry(eventSourceEntry)
      eventSourceEntry.eventSource.close()
    }

    updateStoreState()
    attemptReconnect(providedToken)
  }
}

/**
 * The JWT a token stream currently runs on, for the RPCs that must share its identity. Falls back
 * to the key (the JWT the stream was first opened with); `undefined` on the cookie stream.
 */
export function getStreamCredential(streamKey: StreamKey): string | undefined {
  if (streamKey === undefined) {
    return undefined
  }
  return getEventSourceEntry(streamKey)?.credential ?? streamKey
}

/**
 * Reconnects the stream whose key is `streamKey`, keeping the key so subscriptions stay attached.
 * The cookie stream simply reconnects: the gateway refreshes the cookie. A token stream re-opens
 * with the RPC client's current JWT, which `getToken` renews when it is about to expire; `refresh`
 * discards it first, for a retry after the server rejected the previous credential. If GraphQL
 * cannot be reached, the previous credential is reused so the EventSource error path keeps the
 * retry ladder going instead of this promise rejecting.
 */
export async function reconnect(
  streamKey: StreamKey,
  options: { refresh?: boolean } = {},
): Promise<void> {
  if (streamKey === undefined && streamUsesCookieAuth()) {
    return connect(undefined)
  }
  let credential = getStreamCredential(streamKey)
  try {
    if (options.refresh) {
      invalidateToken()
    }
    await getToken()
    credential = getCurrentToken() || credential
  } catch {
    // keep the previous credential
  }
  return connect(streamKey, credential)
}

function startConnectionHealthCheck(providedToken?: StreamKey) {
  healthCheckInterval = setInterval(() => {
    const eventSourceEntry = getEventSourceEntry(providedToken)
    if (
      !eventSourceEntry?.eventSource ||
      eventSourceEntry.eventSource.readyState === EventSource.CLOSED
    ) {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval)
        healthCheckInterval = null
      }
      attemptReconnect(providedToken)
      return
    }
  }, 30_000)

  monitorConnectionState(providedToken)
}

function monitorConnectionState(providedToken?: StreamKey) {
  stateCheckInterval = setInterval(() => {
    const eventSourceEntry = getEventSourceEntry(providedToken)
    if (!eventSourceEntry?.eventSource) {
      if (stateCheckInterval) {
        clearInterval(stateCheckInterval)
        stateCheckInterval = null
      }
      return
    }

    const currentState = eventSourceEntry.eventSource.readyState

    // If the connection was open but is now closed, attempt to reconnect
    if (currentState === EventSource.CLOSED && getIsConnected(eventSourceEntry.eventSource)) {
      debug && log('Connection state changed to CLOSED, attempting reconnect')
      attemptReconnect(providedToken)
      if (stateCheckInterval) {
        clearInterval(stateCheckInterval)
        stateCheckInterval = null
      }
      return
    }

    if (currentState === EventSource.CONNECTING && eventSourceEntry.state === 'connecting') {
      const connectionTimeout = 10_000
      if (Date.now() - eventSourceEntry.connectionStartTime! > connectionTimeout) {
        debug && log('Connection timeout, attempting reconnect')
        eventSourceEntry.state = 'disconnected'
        patchEventSourceEntry(eventSourceEntry)
        attemptReconnect(providedToken)
        if (stateCheckInterval) {
          clearInterval(stateCheckInterval)
          stateCheckInterval = null
        }
        return
      }
    }
  }, 5000)
}

export function disconnect(providedToken?: StreamKey) {
  const eventSourceEntry = getEventSourceEntry(providedToken)
  if (!eventSourceEntry?.eventSource) {
    return
  }

  debug && log('Disconnecting')
  clearReconnectTimeout()
  clearHealthCheckInterval()
  clearStateCheckInterval()
  reconnectAttempts = 0
  eventSourceEntry.lastError = null

  if (eventSourceEntry.eventSource) {
    eventSourceEntry.eventSource.close()
  }
  eventSourceEntry.state = 'disconnected'
  patchEventSourceEntry(eventSourceEntry)
  updateStoreState()
}

function clearReconnectTimeout() {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
  }
}

function clearHealthCheckInterval() {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval)
    healthCheckInterval = null
  }
}

function clearStateCheckInterval() {
  if (stateCheckInterval) {
    clearInterval(stateCheckInterval)
    stateCheckInterval = null
  }
}

export const MAX_RECONNECT_ATTEMPTS = 5

/**
 * How long a connection has to survive before its next drop is treated as a fresh incident rather
 * than a continuation of the run of failures that preceded it.
 */
export const STABLE_CONNECTION_MS = 30_000

const BASE_RECONNECT_DELAY_MS = 1000

/**
 * Ceiling on the wait between attempts. Unreachable at `MAX_RECONNECT_ATTEMPTS` of 5, where the
 * ladder tops out at 16s — it only starts mattering if that budget grows. Deliberately not shared
 * with `STABLE_CONNECTION_MS`: the two happen to be 30s today but answer different questions, so
 * tying them together would make retuning one silently retune the other.
 */
const MAX_RECONNECT_DELAY_MS = 30_000

/**
 * Decides whether to retry, and how long to wait, from the attempts already spent and how long the
 * connection that just dropped had been up. Returns `null` when the budget is exhausted.
 *
 * The budget exists to stop us hammering an endpoint that is genuinely down, so it has to count
 * *consecutive* failures. Spending it per page load instead means a tab that survives a handful of
 * ordinary network blips stops receiving notifications for the rest of its life, silently — and the
 * backoff has climbed by then, so the last two attempts are 8s and 16s apart. A connection that
 * stayed up for a while is evidence the endpoint is fine, so its next drop starts over.
 *
 * Flapping does not benefit: a connection that is accepted and dropped immediately never reaches
 * `STABLE_CONNECTION_MS`, so the backoff still grows 1s, 2s, 4s, 8s, 16s and then gives up.
 */
export function planReconnect({
  attempts,
  uptimeMs,
}: {
  attempts: number
  uptimeMs: number
}): { attempts: number; delayMs: number } | null {
  const spent = uptimeMs >= STABLE_CONNECTION_MS ? 0 : attempts

  if (spent >= MAX_RECONNECT_ATTEMPTS) {
    return null
  }

  return {
    attempts: spent + 1,
    delayMs: Math.min(BASE_RECONNECT_DELAY_MS * Math.pow(2, spent), MAX_RECONNECT_DELAY_MS),
  }
}

function attemptReconnect(providedToken?: StreamKey) {
  const eventSourceEntry = getEventSourceEntry(providedToken)
  // Don't attempt to reconnect if we're already connecting
  if (eventSourceEntry?.state === 'connecting') {
    return
  }

  // No active `subscribeToEvent` entries for this JWT — usually because every
  // hook unsubscribed (navigation, dependency churn), not because auth is missing.
  if (!hasSubscriptionsForToken(providedToken)) {
    debug &&
      log(
        'Skipping reconnect: no active notification event subscriptions for this token (auth token may still be present)',
      )
    return
  }

  const plan = planReconnect({
    attempts: reconnectAttempts,
    uptimeMs: eventSourceEntry?.connectionStartTime
      ? Date.now() - eventSourceEntry.connectionStartTime
      : 0,
  })

  if (!plan) {
    debug && log('Max reconnection attempts reached')
    return
  }

  clearReconnectTimeout()

  reconnectTimeout = setTimeout(() => {
    reconnectAttempts = plan.attempts
    debug &&
      log(`Attempting reconnect ${plan.attempts}/${MAX_RECONNECT_ATTEMPTS} in ${plan.delayMs}ms`)
    // A first retry after a healthy stretch is the gateway's routine cut; only a repeated failure
    // suggests the server rejected the credential and it must be replaced.
    void reconnect(providedToken, { refresh: plan.attempts > 1 })
  }, plan.delayMs)
}

function updateStoreState() {
  setNotificationsStoreState({
    lastEvent: getLastEvent(),
  })
}

function getIsConnected(eventSource: EventSource): boolean {
  return eventSource?.readyState === EventSource.OPEN
}

function getLastEvent(): NotificationJson | null {
  return lastEvent
}

export function getReconnectAttemptsCount(): number {
  return reconnectAttempts
}

export function resetReconnectAttempts() {
  reconnectAttempts = 0
}

export function isReconnecting(): boolean {
  return reconnectTimeout !== null
}

export function getEventSourceMap() {
  return eventSourceMap
}

export function isConnectedForToken(token?: StreamKey): boolean {
  const eventSourceEntry = getEventSourceEntry(token)
  return (
    eventSourceEntry?.state === 'connected' &&
    eventSourceEntry?.eventSource?.readyState === EventSource.OPEN
  )
}

export function getErrorForToken(token?: StreamKey): Event | null {
  const eventSourceEntry = getEventSourceEntry(token)
  return eventSourceEntry?.lastError || null
}

export function getReconnectAttemptsForToken(token?: StreamKey): number {
  const eventSourceEntry = getEventSourceEntry(token)
  return eventSourceEntry?.reconnectAttempts || 0
}

export const sseConnectionManager = {
  connect,
  disconnect,
  getReconnectAttemptsCount: getReconnectAttemptsCount,
  resetReconnectAttempts,
  isReconnecting,
}
